import { useEffect, useRef } from 'react'

const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const isTurnstileEnabled = Boolean(SITE_KEY)

type TurnstileApi = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string
  remove: (widgetId: string) => void
}

function getTurnstile(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile
}

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (getTurnstile()) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const onError = () => reject(new Error('Turnstile script failed to load'))
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', onError)
      return
    }

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = onError
    document.head.appendChild(script)
  })

  return scriptPromise
}

interface TurnstileWidgetProps {
  onToken: (token: string | null) => void
}

export default function TurnstileWidget({ onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)

  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  useEffect(() => {
    if (!isTurnstileEnabled) return

    let cancelled = false

    loadScript()
      .then(() => {
        const turnstile = getTurnstile()
        const container = containerRef.current
        if (cancelled || !turnstile || !container || widgetIdRef.current) return

        widgetIdRef.current = turnstile.render(container, {
          sitekey: SITE_KEY,
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        })
      })
      .catch(() => {
        // Leave the token empty and let the server decide whether it insists.
        if (!cancelled) onTokenRef.current(null)
      })

    return () => {
      cancelled = true
      const turnstile = getTurnstile()
      if (turnstile && widgetIdRef.current) {
        turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [])

  if (!isTurnstileEnabled) return null

  return <div ref={containerRef} className="flex justify-center" />
}
